import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import { Helmet } from "react-helmet-async";
import heroImg from "/z.png";
import {
  Hammer,
  Building,
  Home,
  Store,
  CheckCircle,
  Users,
  Shield,
} from "lucide-react";

export default function Homepage() {
  const { user } = useAuth();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();

  const handleFindJobsClick = (e: React.MouseEvent) => {
    if (!user) {
      e.preventDefault();
      navigate("/packages");
    }
    // If user is logged in, the normal Link behavior will work
  };

  // Card component for consistent styling
  const FeatureCard = ({
    icon: Icon,
    title,
    description,
    children,
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    children?: React.ReactNode;
  }) => (
    <div
      className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 h-full flex flex-col ${
        isDark
          ? "bg-gray-800 border-gray-700 hover:bg-gray-750"
          : "bg-white border-sky-100 hover:bg-sky-50/50"
      }`}
    >
      <div className="flex items-center mb-4">
        <div
          className={`p-3 rounded-lg mr-4 ${
            isDark
              ? "bg-gradient-to-br from-sky-900 to-blue-900"
              : "bg-gradient-to-br from-sky-100 to-blue-100"
          }`}
        >
          <Icon
            className={`${isDark ? "text-sky-300" : "text-sky-600"}`}
            size={24}
          />
        </div>
        <h3 className="text-xl font-semibold bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
          {title}
        </h3>
      </div>
      <p
        className={`mb-4 flex-grow ${isDark ? "text-gray-300" : "text-gray-600"}`}
      >
        {description}
      </p>
      {children}
    </div>
  );

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 to-gray-800 text-white'
          : 'bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900'
      }`}
    >
      <Helmet>
        <title>FindWork - Find Jobs Near You in Kenya</title>
        <meta
          name="description"
          content="FindWork connects employers with ready-to-work people in their area. No CVs, no waiting — just real workers. Find jobs or hire workers near you today."
        />
        <meta
          name="keywords"
          content="find work Kenya, jobs near me, hire workers Kenya, local jobs, casual jobs Kenya"
        />
        <meta
          property="og:title"
          content="FindWork - Find Jobs Near You in Kenya"
        />
        <meta
          property="og:description"
          content="Connect with employers or find work near you. No CVs required."
        />
        <meta property="og:url" content="https://findwork.ltd" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://findwork.ltd/og-image.jpg" />
        <link rel="canonical" href="https://findwork.ltd" />
      </Helmet>
      
      {/* Hero Section */}
      <section
        className={`relative transition-colors duration-300 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col-reverse md:flex-row items-center py-16">
          {/* Left Content */}
          <div className="w-full md:w-1/2 space-y-6">
            <h4
              className={`text-4xl sm:text-5xl font-extrabold leading-tight ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}
            >
              FindWork connects employers{' '}
              <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
                with ready-to-work people in their area
              </span>{' '}
              No CVs No waiting Just real workers
            </h4>
            <p
              className={`text-lg ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Discover thousands of job opportunities, connect with top
              companies, and build your career path. FindWork makes it simple,
              fast, and reliable.
            </p>
            <div className="flex space-x-4">
              <Link
                to="/employers-packages"
                className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-medium px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
              >
                Hire a Worker
              </Link>
              <Link
                to={user ? '/find-job' : '/packages'}
                onClick={handleFindJobsClick}
                className={`border font-medium px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 ${
                  isDark
                    ? 'border-sky-400 text-sky-400 hover:bg-sky-900/20'
                    : 'border-sky-500 text-sky-600 hover:bg-sky-50'
                }`}
              >
                Find Work Near You
              </Link>
            </div>
          </div>

          {/* Right Image */}
          <div className="w-full md:w-1/2 flex justify-center mb-10 md:mb-0">
            <img
              src={heroImg}
              alt="FindWork Hero"
              className="max-h-96 object-contain drop-shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        className={`py-16 transition-colors duration-300 ${
          isDark
            ? 'bg-gradient-to-br from-gray-800 to-gray-700'
            : 'bg-gradient-to-br from-sky-100 to-blue-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className={`text-3xl font-bold mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Why Choose{' '}
            <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
              FindWork?
            </span>
          </h2>
          <p
            className={`mb-12 max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            We connect talented individuals with the best companies. Our
            platform is designed to make Find-Working easier and more effective.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Trust Strip Card */}
            <FeatureCard
              icon={Shield}
              title="Trusted Platform"
              description="Trusted by local businesses, homes, and contractors across various industries."
            >
              <div className="flex flex-wrap gap-2 mt-2">
                <div
                  className={`flex items-center space-x-2 px-3 py-2 rounded-full ${
                    isDark ? 'bg-gray-700' : 'bg-sky-50'
                  }`}
                >
                  <Home className="text-sky-500 dark:text-sky-300" size={16} />
                  <span
                    className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    Homes
                  </span>
                </div>
                <div
                  className={`flex items-center space-x-2 px-3 py-2 rounded-full ${
                    isDark ? 'bg-gray-700' : 'bg-sky-50'
                  }`}
                >
                  <Hammer
                    className="text-sky-500 dark:text-sky-300"
                    size={16}
                  />
                  <span
                    className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    Contractors
                  </span>
                </div>
                <div
                  className={`flex items-center space-x-2 px-3 py-2 rounded-full ${
                    isDark ? 'bg-gray-700' : 'bg-sky-50'
                  }`}
                >
                  <Building
                    className="text-sky-500 dark:text-sky-300"
                    size={16}
                  />
                  <span
                    className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    Businesses
                  </span>
                </div>
                <div
                  className={`flex items-center space-x-2 px-3 py-2 rounded-full ${
                    isDark ? 'bg-gray-700' : 'bg-sky-50'
                  }`}
                >
                  <Store className="text-sky-500 dark:text-sky-300" size={16} />
                  <span
                    className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    Shops
                  </span>
                </div>
              </div>
            </FeatureCard>

            {/* Easy Application Card */}
            <FeatureCard
              icon={CheckCircle}
              title="Easy Application"
              description="Apply for jobs in just a few clicks with our streamlined application process. No CVs required - just your skills and availability."
            />

            {/* Trusted by Companies Card */}
            <FeatureCard
              icon={Users}
              title="Trusted by Companies"
              description="Top employers trust FindWork to find the best candidates for their teams. Join thousands of satisfied businesses."
            />
          </div>
        </div>
      </section>
    </div>
  );
}
